/**
 * Saved Colours — guest/local storage layer.
 *
 * Owns the localStorage-backed state (colours + groups) and is the single
 * source of truth for reads/writes on the client. Works with no logged-in
 * customer; the sync layer (saved-colours-sync.js) later reconciles this
 * state with the customer account.
 *
 * State shape (schemaVersion 1):
 * {
 *   schemaVersion: 1,
 *   updatedAt: <ISO string>,
 *   groups:  [{ id, name, createdAt, isDefault }],
 *   colours: [{ productId, handle, name, hex, savedAt, groupIds: [] }],
 *   deletedColours: [{ productId, deletedAt: <ISO string> }],
 *   deletedGroupIds: [<group id>]
 * }
 *
 * deletedColours/deletedGroupIds are tombstones: saved-colours-merge.js needs
 * a record that something was *deleted* (not just "absent"), or a remote
 * copy that hasn't caught up yet keeps winning a plain union merge and
 * resurrecting it. A colour's tombstone is cleared the moment it's saved
 * again (see saveColour below); a merge drops it as stale once it sees the
 * colour saved after the removal (see mergeStates in saved-colours-merge.js).
 * Group ids are never reused, so their tombstones don't need clearing.
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'haymes:saved-colours:v1';
  var SCHEMA_VERSION = 1;
  var DEFAULT_GROUP_ID = 'default';
  var MAX_RECOMMENDED_COLOURS = 400;
  var TOMBSTONE_RETENTION_MS = 1000 * 60 * 60 * 24 * 180; // 180 days

  var listeners = [];
  var memoryState = null; // in-memory fallback when localStorage is unavailable
  var storageAvailable = detectStorageAvailability();

  function detectStorageAvailability() {
    try {
      var testKey = '__saved_colours_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function uid(prefix) {
    return (
      (prefix || 'id') +
      '_' +
      Date.now().toString(36) +
      Math.random().toString(36).slice(2, 8)
    );
  }

  function createDefaultState() {
    return {
      schemaVersion: SCHEMA_VERSION,
      updatedAt: nowIso(),
      groups: [
        {
          id: DEFAULT_GROUP_ID,
          name: 'All Saved Colours',
          createdAt: nowIso(),
          isDefault: true,
        },
      ],
      colours: [],
      deletedColours: [],
      deletedGroupIds: [],
    };
  }

  // Tombstones older than this can no longer influence a merge against any
  // remote copy that isn't absurdly stale, so drop them rather than let the
  // list grow forever.
  function pruneDeletedColours(deletedColours) {
    var cutoff = Date.now() - TOMBSTONE_RETENTION_MS;
    return deletedColours.filter(function (t) {
      var deletedAt = t && t.deletedAt ? new Date(t.deletedAt).getTime() : 0;
      return deletedAt >= cutoff;
    });
  }

  /**
   * Upgrade older persisted shapes to the current schema. Bump
   * SCHEMA_VERSION and add a case here whenever the shape changes.
   */
  function migrate(rawState) {
    if (!rawState || typeof rawState !== 'object') {
      return createDefaultState();
    }

    var state = rawState;

    if (!state.schemaVersion) {
      // Pre-versioned or malformed data: rebuild rather than trust it.
      return createDefaultState();
    }

    if (state.schemaVersion < SCHEMA_VERSION) {
      // No prior versions exist yet; placeholder for future migrations.
      state.schemaVersion = SCHEMA_VERSION;
    }

    if (!Array.isArray(state.groups) || !state.groups.some(function (g) { return g.isDefault; })) {
      state.groups = [
        {
          id: DEFAULT_GROUP_ID,
          name: 'All Saved Colours',
          createdAt: nowIso(),
          isDefault: true,
        },
      ].concat(Array.isArray(state.groups) ? state.groups.filter(function (g) { return !g.isDefault; }) : []);
    } else {
      // The default group's label was renamed from "Saved Colours" to
      // "All Saved Colours" (it's the master list every group filters on
      // top of, not just one group among others) — carry that rename
      // forward for customers whose default group was already persisted
      // under the old name, rather than only applying it to brand-new state.
      state.groups.forEach(function (g) {
        if (g.isDefault && g.name === 'Saved Colours') g.name = 'All Saved Colours';
      });
    }

    if (!Array.isArray(state.colours)) {
      state.colours = [];
    }

    if (!Array.isArray(state.deletedGroupIds)) {
      state.deletedGroupIds = [];
    }

    state.colours = state.colours
      .filter(function (c) { return c && c.productId != null; })
      .map(function (c) {
        return {
          productId: String(c.productId),
          handle: c.handle || '',
          name: c.name || '',
          hex: c.hex || '',
          savedAt: c.savedAt || nowIso(),
          // A colour is allowed to belong to zero groups (see
          // removeFromGroup()'s doc comment) — only fall back to the
          // default group when groupIds is missing/malformed, never just
          // because it's legitimately empty. migrate() runs on every
          // read(), so getting this wrong snapped a deliberately-emptied
          // colour straight back into "Saved Colours" the instant it was
          // unchecked.
          groupIds: Array.isArray(c.groupIds) ? c.groupIds : [DEFAULT_GROUP_ID],
        };
      });

    if (!Array.isArray(state.deletedColours)) {
      state.deletedColours = [];
    }
    state.deletedColours = pruneDeletedColours(
      state.deletedColours
        .filter(function (t) { return t && t.productId != null && t.deletedAt; })
        .map(function (t) { return { productId: String(t.productId), deletedAt: t.deletedAt }; })
    );

    return state;
  }

  function read() {
    if (!storageAvailable) {
      return memoryState || createDefaultState();
    }
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return createDefaultState();
      return migrate(JSON.parse(raw));
    } catch (error) {
      return createDefaultState();
    }
  }

  function write(state) {
    state.updatedAt = nowIso();
    state.schemaVersion = SCHEMA_VERSION;

    if (!storageAvailable) {
      memoryState = state;
      notify(state);
      return { ok: true, fallback: 'memory' };
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      notify(state);
      return { ok: true };
    } catch (error) {
      // Quota exceeded or storage revoked mid-session: keep working in memory.
      memoryState = state;
      storageAvailable = false;
      notify(state);
      return { ok: true, fallback: 'memory', error: error };
    }
  }

  function notify(state) {
    listeners.forEach(function (fn) {
      try {
        fn(state);
      } catch (error) {
        /* listener errors must never break storage */
      }
    });
  }

  function subscribe(fn) {
    listeners.push(fn);
    return function unsubscribe() {
      var index = listeners.indexOf(fn);
      if (index > -1) listeners.splice(index, 1);
    };
  }

  function getState() {
    return read();
  }

  function setState(state) {
    return write(state);
  }

  function isColourSaved(productId) {
    var state = read();
    return state.colours.some(function (c) { return c.productId === String(productId); });
  }

  function getColour(productId) {
    var state = read();
    return state.colours.find(function (c) { return c.productId === String(productId); }) || null;
  }

  function saveColour(colour, groupIds) {
    var state = read();
    var id = String(colour.productId);
    var existing = state.colours.find(function (c) { return c.productId === id; });
    var targetGroupIds = groupIds && groupIds.length ? groupIds : [DEFAULT_GROUP_ID];

    if (existing) {
      existing.groupIds = Array.from(new Set(existing.groupIds.concat(targetGroupIds)));
      existing.name = colour.name || existing.name;
      existing.hex = colour.hex || existing.hex;
      existing.handle = colour.handle || existing.handle;
    } else {
      state.colours.push({
        productId: id,
        handle: colour.handle || '',
        name: colour.name || '',
        hex: colour.hex || '',
        savedAt: nowIso(),
        groupIds: targetGroupIds,
      });
    }

    // Saving supersedes any earlier removal of this same colour — without
    // this, a stale tombstone from a prior remove would still be sitting in
    // state.deletedColours and could confuse a later merge.
    state.deletedColours = (state.deletedColours || []).filter(function (t) { return t.productId !== id; });

    write(state);
    return { ok: true, isNearLimit: state.colours.length >= MAX_RECOMMENDED_COLOURS };
  }

  function removeColour(productId) {
    var state = read();
    var id = String(productId);
    state.colours = state.colours.filter(function (c) { return c.productId !== id; });

    // Record *that* this colour was deleted, not just its absence — a plain
    // union merge against a remote copy that hasn't caught up yet would
    // otherwise treat "not present locally" as "never existed" and bring it
    // straight back. See saved-colours-merge.js.
    state.deletedColours = (state.deletedColours || []).filter(function (t) { return t.productId !== id; });
    state.deletedColours.push({ productId: id, deletedAt: nowIso() });

    write(state);
  }

  function toggleColour(colour, groupIds) {
    if (isColourSaved(colour.productId)) {
      removeColour(colour.productId);
      return { saved: false };
    }
    saveColour(colour, groupIds);
    return { saved: true };
  }

  function assignToGroup(productId, groupId) {
    var state = read();
    var id = String(productId);
    var colour = state.colours.find(function (c) { return c.productId === id; });
    if (!colour) return { ok: false, error: 'not-found' };
    if (colour.groupIds.indexOf(groupId) === -1) {
      colour.groupIds.push(groupId);
      write(state);
    }
    return { ok: true };
  }

  /**
   * Removes a colour from a group, including the default "Saved Colours"
   * group when explicitly requested (e.g. unchecking it in the group
   * picker) — a colour is allowed to end up in zero groups; it still
   * shows under "All groups" on the Saved Colours page, just not under
   * any specific one. This is deliberately different from the cascade in
   * deleteGroup(), which re-homes orphaned colours to the default group
   * because their group no longer exists at all.
   */
  function removeFromGroup(productId, groupId) {
    var state = read();
    var id = String(productId);
    var colour = state.colours.find(function (c) { return c.productId === id; });
    if (!colour) return { ok: false, error: 'not-found' };
    colour.groupIds = colour.groupIds.filter(function (g) { return g !== groupId; });
    write(state);
    return { ok: true };
  }

  function addGroup(name) {
    var trimmed = (name || '').trim();
    if (!trimmed) return { ok: false, error: 'empty-name' };

    var state = read();
    var duplicate = state.groups.some(function (g) {
      return g.name.toLowerCase() === trimmed.toLowerCase();
    });
    if (duplicate) return { ok: false, error: 'duplicate-name' };

    var group = { id: uid('grp'), name: trimmed, createdAt: nowIso(), isDefault: false };
    state.groups.push(group);
    write(state);
    return { ok: true, group: group };
  }

  function renameGroup(groupId, name) {
    var trimmed = (name || '').trim();
    if (!trimmed) return { ok: false, error: 'empty-name' };

    var state = read();
    var group = state.groups.find(function (g) { return g.id === groupId; });
    if (!group) return { ok: false, error: 'not-found' };
    if (group.isDefault) return { ok: false, error: 'default-group-locked' };

    group.name = trimmed;
    write(state);
    return { ok: true };
  }

  function deleteGroup(groupId) {
    var state = read();
    var group = state.groups.find(function (g) { return g.id === groupId; });
    if (!group) return { ok: false, error: 'not-found' };
    if (group.isDefault) return { ok: false, error: 'default-group-locked' };

    state.groups = state.groups.filter(function (g) { return g.id !== groupId; });
    state.colours.forEach(function (c) {
      // Only re-home a colour that this specific deletion actually
      // orphaned. Without the `hadThisGroup` check, a colour already
      // sitting at zero groups (a deliberate, valid state) got swept back
      // into "Saved Colours" by deleting ANY group, even one it never
      // belonged to.
      var hadThisGroup = c.groupIds.indexOf(groupId) !== -1;
      c.groupIds = c.groupIds.filter(function (g) { return g !== groupId; });
      if (hadThisGroup && !c.groupIds.length) c.groupIds = [DEFAULT_GROUP_ID];
    });
    if (!Array.isArray(state.deletedGroupIds)) state.deletedGroupIds = [];
    if (state.deletedGroupIds.indexOf(groupId) === -1) state.deletedGroupIds.push(groupId);
    write(state);
    return { ok: true };
  }

  /**
   * Re-orders state.groups to match orderedGroupIds. Used by the group
   * manager's move-up/move-down controls; write() already notifies
   * subscribers, so the existing customer-sync push (saved-colours-sync.js)
   * persists the new order with no separate API call needed.
   */
  function reorderGroups(orderedGroupIds) {
    var state = read();
    var byId = {};
    state.groups.forEach(function (g) { byId[g.id] = g; });
    var reordered = orderedGroupIds.map(function (id) { return byId[id]; }).filter(Boolean);
    // Defensive: any group not present in orderedGroupIds (shouldn't happen
    // in practice) is appended rather than silently dropped.
    state.groups.forEach(function (g) { if (reordered.indexOf(g) === -1) reordered.push(g); });
    state.groups = reordered;
    write(state);
    return { ok: true };
  }

  global.SavedColoursStorage = {
    STORAGE_KEY: STORAGE_KEY,
    SCHEMA_VERSION: SCHEMA_VERSION,
    DEFAULT_GROUP_ID: DEFAULT_GROUP_ID,
    MAX_RECOMMENDED_COLOURS: MAX_RECOMMENDED_COLOURS,
    isStorageAvailable: function () { return storageAvailable; },
    createDefaultState: createDefaultState,
    getState: getState,
    setState: setState,
    subscribe: subscribe,
    isColourSaved: isColourSaved,
    getColour: getColour,
    saveColour: saveColour,
    removeColour: removeColour,
    toggleColour: toggleColour,
    assignToGroup: assignToGroup,
    removeFromGroup: removeFromGroup,
    addGroup: addGroup,
    renameGroup: renameGroup,
    deleteGroup: deleteGroup,
    reorderGroups: reorderGroups,
    uid: uid,
  };
})(window);
